import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot, Shield, Plus, Trash2, Eye, Loader2, AlertCircle,
  CheckCircle, Clock, ToggleLeft, ToggleRight, FileText, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CrawlerSettingsPanel = ({ token }) => {
  const [crawlers, setCrawlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Custom agent form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: '', allowed: false, description: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const loadCrawlers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/crawler-settings/list`, { headers });
      if (!res.ok) throw new Error('Failed to load settings');
      const data = await res.json();
      setCrawlers(data.crawlers || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [token]);

  const loadAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/crawler-settings/audit-log?limit=10`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.logs || []);
      }
    } catch (e) { /* silent */ }
    setAuditLoading(false);
  }, [token]);

  useEffect(() => {
    loadCrawlers();
    loadAuditLogs();
  }, [loadCrawlers, loadAuditLogs]);

  const handleToggle = async (name, currentAllowed) => {
    setSaving(name);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/crawler-settings/toggle`, {
        method: 'POST', headers,
        body: JSON.stringify({ name, allowed: !currentAllowed }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update');
      }
      setCrawlers(prev => prev.map(c => c.name === name ? { ...c, allowed: !currentAllowed } : c));
      loadAuditLogs();
    } catch (e) {
      setError(e.message);
    }
    setSaving(null);
  };

  const handleAddAgent = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError('');
    try {
      const res = await fetch(`${API_URL}/api/crawler-settings/add-custom`, {
        method: 'POST', headers,
        body: JSON.stringify(newAgent),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to add crawler');
      }
      setShowAddForm(false);
      setNewAgent({ name: '', allowed: false, description: '' });
      loadCrawlers();
      loadAuditLogs();
    } catch (e) {
      setAddError(e.message);
    }
    setAddLoading(false);
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete crawler "${name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/crawler-settings/delete/${encodeURIComponent(name)}`, {
        method: 'DELETE', headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete');
      }
      loadCrawlers();
      loadAuditLogs();
    } catch (e) {
      setError(e.message);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const res = await fetch(`${API_URL}/api/crawler-settings/preview-robots-txt`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPreview(data.content);
      }
    } catch (e) { /* silent */ }
    setPreviewLoading(false);
  };

  const defaultCrawlers = crawlers.filter(c => c.is_default);
  const customCrawlers = crawlers.filter(c => !c.is_default);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
              <Bot size={20} className="text-navy" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-navy" data-testid="crawler-settings-title">SEO & AI Crawler Controls</h2>
              <p className="text-sm text-slate-500">Manage which AI crawlers can access your site content</p>
            </div>
          </div>
          <button
            onClick={loadPreview}
            data-testid="preview-robots-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors"
          >
            <Eye size={16} />
            Preview robots.txt
          </button>
        </div>
        <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
          <p className="text-xs text-blue-700">
            <strong>Note:</strong> Standard search engines (Googlebot, Bingbot) are always allowed and cannot be blocked from here. 
            These controls only affect AI-specific crawlers. Changes take effect immediately.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
          <AlertCircle size={18} className="text-red-500" />
          <p className="text-red-700 text-sm">{error}</p>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600"><X size={16} /></button>
        </div>
      )}

      {/* Default AI Crawlers */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-semibold text-navy flex items-center gap-2">
            <Shield size={18} />
            AI Crawler Controls
          </h3>
        </div>
        <div className="divide-y divide-slate-100">
          {defaultCrawlers.map((crawler) => (
            <div key={crawler.name} className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid={`crawler-row-${crawler.name}`}>
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-navy">{crawler.name}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                    crawler.allowed
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {crawler.allowed ? 'Allowed' : 'Blocked'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{crawler.description}</p>
              </div>
              <button
                onClick={() => handleToggle(crawler.name, crawler.allowed)}
                disabled={saving === crawler.name}
                data-testid={`crawler-toggle-${crawler.name}`}
                className="flex-shrink-0"
              >
                {saving === crawler.name ? (
                  <Loader2 size={28} className="animate-spin text-slate-400" />
                ) : crawler.allowed ? (
                  <ToggleRight size={36} className="text-green-500 hover:text-green-600 transition-colors" />
                ) : (
                  <ToggleLeft size={36} className="text-slate-300 hover:text-slate-400 transition-colors" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Agents */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-navy flex items-center gap-2">
            <Plus size={18} />
            Custom Agents
          </h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            data-testid="add-custom-crawler-btn"
            className="flex items-center gap-2 px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors"
          >
            <Plus size={14} />
            Add Agent
          </button>
        </div>

        {showAddForm && (
          <div className="p-5 bg-slate-50 border-b border-slate-100">
            <form onSubmit={handleAddAgent} className="space-y-3" data-testid="add-crawler-form">
              {addError && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle size={14} />
                  {addError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                  placeholder="Agent name (e.g. MyBot)"
                  required
                  data-testid="new-crawler-name-input"
                  className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy/40 outline-none"
                />
                <input
                  type="text"
                  value={newAgent.description}
                  onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                  placeholder="Description (optional)"
                  data-testid="new-crawler-desc-input"
                  className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy/40 outline-none"
                />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newAgent.allowed}
                      onChange={(e) => setNewAgent({ ...newAgent, allowed: e.target.checked })}
                      data-testid="new-crawler-allowed-checkbox"
                      className="w-4 h-4 rounded border-slate-300 text-navy focus:ring-navy"
                    />
                    <span className="text-sm text-slate-600">Allow access</span>
                  </label>
                  <button
                    type="submit"
                    disabled={addLoading}
                    data-testid="save-new-crawler-btn"
                    className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 disabled:opacity-50 transition-colors"
                  >
                    {addLoading ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); setAddError(''); }}
                    className="px-3 py-2 text-slate-500 hover:text-slate-700 text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {customCrawlers.length === 0 && !showAddForm ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            No custom agents added yet. Click "Add Agent" to add one.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {customCrawlers.map((crawler) => (
              <div key={crawler.name} className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid={`crawler-row-${crawler.name}`}>
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-navy">{crawler.name}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                      crawler.allowed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {crawler.allowed ? 'Allowed' : 'Blocked'}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Custom</span>
                  </div>
                  {crawler.description && (
                    <p className="text-xs text-slate-500 mt-1">{crawler.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggle(crawler.name, crawler.allowed)}
                    disabled={saving === crawler.name}
                    data-testid={`crawler-toggle-${crawler.name}`}
                  >
                    {saving === crawler.name ? (
                      <Loader2 size={28} className="animate-spin text-slate-400" />
                    ) : crawler.allowed ? (
                      <ToggleRight size={32} className="text-green-500 hover:text-green-600 transition-colors" />
                    ) : (
                      <ToggleLeft size={32} className="text-slate-300 hover:text-slate-400 transition-colors" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(crawler.name)}
                    data-testid={`crawler-delete-${crawler.name}`}
                    className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                    title="Delete crawler"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-semibold text-navy flex items-center gap-2">
            <Clock size={18} />
            Crawler Settings Change Log
          </h3>
        </div>
        {auditLoading ? (
          <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
        ) : auditLogs.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No changes recorded yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {auditLogs.map((log, i) => {
              const details = log.details || {};
              let actionText = '';
              let actionColor = 'text-slate-600';

              if (log.action === 'CRAWLER_SETTING_UPDATED') {
                const status = details.new_allowed ? 'Allowed' : 'Blocked';
                actionText = `${status} ${details.crawler_name}`;
                actionColor = details.new_allowed ? 'text-green-700' : 'text-red-700';
              } else if (log.action === 'CRAWLER_AGENT_ADDED') {
                actionText = `Added custom agent: ${details.crawler_name}`;
                actionColor = 'text-blue-700';
              } else if (log.action === 'CRAWLER_AGENT_DELETED') {
                actionText = `Deleted agent: ${details.crawler_name}`;
                actionColor = 'text-red-700';
              }

              return (
                <div key={i} className="px-6 py-3 flex items-center gap-4 text-sm" data-testid={`audit-log-entry-${i}`}>
                  <span className="text-slate-400 text-xs whitespace-nowrap w-36">
                    {new Date(log.timestamp).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="text-slate-500 w-40 truncate">{log.admin_email}</span>
                  <span className={`font-medium ${actionColor}`}>{actionText}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* robots.txt Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-navy" />
                <h3 className="font-semibold text-navy">Current robots.txt Output</h3>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-auto flex-1" data-testid="robots-txt-preview">
              {previewLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
              ) : (
                <pre className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 whitespace-pre-wrap leading-relaxed">
                  {preview}
                </pre>
              )}
            </div>
            <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">
              <p className="text-xs text-slate-500">
                This is the exact content served at <span className="font-mono">thirdcultureleadership.com/robots.txt</span>. 
                Changes take effect immediately when you toggle a crawler above.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrawlerSettingsPanel;
